import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useCan, usePos } from "@/app/context";
import { MoneyText } from "@/components/MoneyText";
import { call } from "@/lib/api";

/**
 * The terms of this consignor (T07-32): commission, who pays the card fee (in plain words), how
 * they are paid, and what they get from ¥10,000 (consignment-service's own preview) — the terms
 * belong to consignment-service (one place to change them); staff without `consignment.read` see
 * that the contract's terms apply.
 */
export function TermsPanel({ consignorId }: { consignorId: string }) {
  const { t } = useTranslation();
  const { consignment } = usePos();
  const canRead = useCan("consignment.read");
  const detail = useQuery({
    queryKey: ["pos-consignors", "detail", consignorId],
    queryFn: () =>
      call(() =>
        consignment.GET("/v1/admin/consignors/{consignor_id}", {
          params: { path: { consignor_id: consignorId } },
        }),
      ),
    enabled: canRead,
    staleTime: 60_000,
  });
  const preview = useQuery({
    queryKey: ["pos-commission", "detail", consignorId],
    queryFn: () =>
      call(() =>
        consignment.POST("/v1/admin/commission-rules/preview", {
          body: { soldPrice: { amount: "10000", currencyCode: "JPY" }, consignorId },
        }),
      ),
    enabled: canRead,
    staleTime: 60_000,
  });
  const c = detail.data;
  return (
    <section aria-labelledby="terms-title" className="flex flex-col gap-2 rounded-lg border p-3">
      <h2 id="terms-title" className="flex items-center gap-2 text-lg font-semibold">
        <FileText aria-hidden className="size-5" />
        {t("consign.terms_title")}
      </h2>
      {!canRead ? (
        <p>{t("consign.terms_contract")}</p>
      ) : c ? (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
          <dt className="text-muted-foreground">{t("consign.commission")}</dt>
          <dd>
            {preview.data
              ? t("consign.commission_value", { percent: preview.data.percent })
              : c.defaultCommissionPercent
                ? t("consign.commission_value", { percent: c.defaultCommissionPercent })
                : t("consign.commission_rule")}
          </dd>
          <dt className="text-muted-foreground">{t("consign.fee_policy")}</dt>
          <dd>{t(`consign.fee_${c.paymentFeePolicy}`)}</dd>
          <dt className="text-muted-foreground">{t("consign.payout")}</dt>
          <dd>{t(`consign.payout_${c.payoutMethod}`)}</dd>
          {preview.data ? (
            <>
              <dt className="text-muted-foreground">{t("consign.example")}</dt>
              <dd>
                <MoneyText amount={Number.parseInt(preview.data.consignorReceives.amount, 10)} />{" "}
                {t("consign.example_note")}
              </dd>
            </>
          ) : null}
        </dl>
      ) : (
        <p className="text-muted-foreground">{t("app.loading")}</p>
      )}
    </section>
  );
}
